import React, { useState, useEffect } from 'react';
import { getTrainingSessions, getTrainingSession, adminCreateTraining, adminUpdateTraining, adminDeleteTraining } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Calendar, Users, Eye } from 'lucide-react';

const AdminTraining = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailSession, setDetailSession] = useState(null);
  const [form, setForm] = useState({ date: '', time: '18:00', duration_minutes: '120', max_participants: '12', min_participants: '4', description: '' });

  const fetchData = async () => {
    try {
      const res = await getTrainingSessions();
      setSessions(res.data);
    } catch (error) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);
  const resetForm = () => setForm({ date: '', time: '18:00', duration_minutes: '120', max_participants: '12', min_participants: '4', description: '' });

  const handleSave = async () => {
    try {
      const data = { ...form, duration_minutes: parseInt(form.duration_minutes), max_participants: parseInt(form.max_participants), min_participants: parseInt(form.min_participants) };
      if (editing) {
        await adminUpdateTraining(editing.session_id, data);
        toast.success('Aktualisiert');
      } else {
        await adminCreateTraining(data);
        toast.success('Erstellt');
      }
      setDialogOpen(false);
      setEditing(null);
      resetForm();
      fetchData();
    } catch (error) { toast.error('Fehler'); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm('Training löschen?')) return;
    try {
      await adminDeleteTraining(s.session_id);
      toast.success('Gelöscht');
      fetchData();
    } catch (error) { toast.error('Fehler'); }
  };

  const handleShowDetail = async (s) => {
    try {
      const res = await getTrainingSession(s.session_id);
      setDetailSession(res.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
  };

  return (
    <div data-testid="admin-training-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">Training</h1>
        <Button onClick={() => { setEditing(null); resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-training-btn">
          <Plus className="w-4 h-4 mr-2" />Training
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <Card key={i} className="card-surface animate-pulse">
              <CardContent className="p-6"><div className="h-16 bg-white/10 rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : sessions.length > 0 ? (
        <div className="space-y-4">
          {sessions.map(s => (
            <Card key={s.session_id} className="card-surface" data-testid={`admin-training-card-${s.session_id}`}>
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-sm flex flex-col items-center justify-center">
                    <span className="text-lg font-heading font-bold text-primary">{new Date(s.date).getDate()}</span>
                    <span className="text-xs text-text-secondary">{new Date(s.date).toLocaleDateString('de-DE', { month: 'short' })}</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold uppercase">{s.description || 'Training'}</h3>
                    <p className="text-text-secondary text-sm">{s.time} Uhr &middot; {s.duration_minutes} Min.</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Users className="w-3.5 h-3.5 text-accent" />
                      <span className="text-sm text-accent font-medium">
                        {s.current_participants || 0}/{s.max_participants || 12} Teilnehmer
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="text-accent" onClick={() => handleShowDetail(s)} data-testid={`view-training-regs-${s.session_id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => {
                    setEditing(s);
                    setForm({
                      date: s.date,
                      time: s.time,
                      duration_minutes: s.duration_minutes?.toString() || '120',
                      max_participants: s.max_participants?.toString() || '12',
                      min_participants: s.min_participants?.toString() || '4',
                      description: s.description || ''
                    });
                    setDialogOpen(true);
                  }}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(s)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-surface">
          <CardContent className="p-8 text-center">
            <Calendar className="w-12 h-12 text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">Keine Trainings vorhanden.</p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle>{editing ? 'Training bearbeiten' : 'Neues Training'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Datum *</Label><Input type="date" className="input-dark" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="training-date-input" /></div>
              <div className="space-y-2"><Label>Uhrzeit *</Label><Input type="time" className="input-dark" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Dauer (Min.)</Label><Input type="number" className="input-dark" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} /></div>
              <div className="space-y-2"><Label>Min.</Label><Input type="number" className="input-dark" value={form.min_participants} onChange={e => setForm({ ...form, min_participants: e.target.value })} /></div>
              <div className="space-y-2"><Label>Max.</Label><Input type="number" className="input-dark" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Beschreibung</Label><Input className="input-dark" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <Button onClick={handleSave} className="btn-primary w-full" data-testid="save-training-btn">{editing ? 'Aktualisieren' : 'Erstellen'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Registrations Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader>
            <DialogTitle>
              Anmeldungen - {detailSession?.description || 'Training'} ({formatDate(detailSession?.date)})
            </DialogTitle>
          </DialogHeader>
          {detailSession && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                <span className="font-medium">{detailSession.current_participants || 0}/{detailSession.max_participants || 12} Teilnehmer</span>
              </div>
              {detailSession.registrations && detailSession.registrations.length > 0 ? (
                <div className="space-y-2">
                  {detailSession.registrations.map((reg, i) => (
                    <div key={reg.registration_id || i} className="card-elevated p-3 flex items-center justify-between">
                      <span className="font-medium">{reg.user_name}</span>
                      <span className="text-xs text-text-secondary">{formatDate(reg.created_at)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-text-secondary text-sm">Noch keine Anmeldungen.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTraining;
